import { useState, useMemo, useEffect } from 'react';
import { useReadContract } from 'wagmi';
import { COHORT_ABI } from '../contract/abi';
import { PieChart } from './PieChart';

// Default multipliers: 0=0.25x, 1-5=level
export const DEFAULT_MULTIPLIERS = [0.25, 1, 2, 3, 4, 5];

// Multiplier input that allows empty field while typing
function MultiplierInput({
  value,
  onChange,
  label
}: {
  value: number;
  onChange: (v: number) => void;
  label: string;
}) {
  const [localValue, setLocalValue] = useState(value.toString());

  // Sync from parent when value changes externally (e.g., reset)
  useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  return (
    <div className="multiplier-input">
      <label>{label}</label>
      <input
        type="number"
        min="0"
        step="0.25"
        value={localValue}
        onChange={(e) => {
          setLocalValue(e.target.value);
          const parsed = parseFloat(e.target.value);
          if (!isNaN(parsed)) {
            onChange(parsed);
          }
        }}
        onBlur={() => {
          const parsed = parseFloat(localValue);
          if (isNaN(parsed) || localValue === '') {
            setLocalValue('0');
            onChange(0);
          }
        }}
      />
    </div>
  );
}

export interface DistributionEntry {
  index: number;
  share: bigint;
  percentage: number;
  name: string;
}

interface DistributionPreviewProps {
  cohortId: `0x${string}`;
  contractAddress: `0x${string}`;
  participantNames: string[];
  multipliers: number[];
  onMultipliersChange: (multipliers: number[]) => void;
  showAdvancedSettings?: boolean;
  advancedSettingsOpen?: boolean;
  onAdvancedSettingsToggle?: (open: boolean) => void;
  showResetButton?: boolean;
  size?: number;
}

export function DistributionPreview({
  cohortId,
  contractAddress,
  participantNames,
  multipliers,
  onMultipliersChange,
  showAdvancedSettings = true,
  advancedSettingsOpen,
  onAdvancedSettingsToggle,
  showResetButton = false,
  size = 180,
}: DistributionPreviewProps) {
  // Scale multipliers to integers for contract (multiply by 100)
  const scaledMultipliers = useMemo(() =>
    multipliers.map(m => BigInt(Math.round(m * 100))) as [bigint, bigint, bigint, bigint, bigint, bigint],
    [multipliers]
  );

  // Get distribution data - use placeholderData to keep previous results visible during refetch
  const { data: distributionData } = useReadContract({
    address: contractAddress,
    abi: COHORT_ABI,
    functionName: 'getDistribution',
    args: [cohortId, scaledMultipliers],
    query: {
      placeholderData: (previousData) => previousData,
    },
  });

  // Calculate distribution from contract data
  const distribution = useMemo(() => {
    if (!distributionData) return [];
    const shares = distributionData[0] as bigint[];
    const total = distributionData[1] as bigint;

    return shares.map((share, index) => {
      const percentage = total > 0n ? (Number(share) / Number(total)) * 100 : 0;
      const name = participantNames[index] || `Participant ${index + 1}`;
      return { index, share, percentage, name };
    });
  }, [distributionData, participantNames]);

  // Prepare pie chart data
  const pieChartData = useMemo(() => {
    return distribution.map(d => ({
      label: d.name,
      value: d.percentage,
    }));
  }, [distribution]);

  if (distribution.length === 0) {
    return null;
  }

  return (
    <div className="distribution-preview">
      <h3>Distribution Preview</h3>
      <PieChart data={pieChartData} size={size} />

      {showAdvancedSettings && (
        <details
          className="advanced-settings"
          open={advancedSettingsOpen}
          onToggle={onAdvancedSettingsToggle ? (e) => onAdvancedSettingsToggle((e.target as HTMLDetailsElement).open) : undefined}
        >
          <summary>Advanced Settings</summary>
          <div className="multiplier-inputs" style={{ marginTop: 'var(--space-md)' }}>
            {multipliers.map((value, index) => (
              <MultiplierInput
                key={index}
                value={value}
                label={`Level ${index}`}
                onChange={(v) => {
                  const newMultipliers = [...multipliers];
                  newMultipliers[index] = v;
                  onMultipliersChange(newMultipliers);
                }}
              />
            ))}
          </div>
          <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 'var(--space-sm)' }}>
            Adjust how importance levels affect the final distribution. Default: 0=0.25×, 1-5=level×
          </p>
          {showResetButton && (
            <button
              onClick={() => onMultipliersChange(DEFAULT_MULTIPLIERS)}
              className="btn-secondary btn-small"
              style={{ marginTop: 'var(--space-sm)' }}
            >
              Reset to Defaults
            </button>
          )}
        </details>
      )}
    </div>
  );
}

// Hook to get distribution data for components that need it separately
export function useDistribution(
  cohortId: `0x${string}`,
  contractAddress: `0x${string}`,
  multipliers: number[],
  participantNames: string[],
  enabled: boolean = true
) {
  const scaledMultipliers = useMemo(() =>
    multipliers.map(m => BigInt(Math.round(m * 100))) as [bigint, bigint, bigint, bigint, bigint, bigint],
    [multipliers]
  );

  const { data: distributionData, refetch } = useReadContract({
    address: contractAddress,
    abi: COHORT_ABI,
    functionName: 'getDistribution',
    args: [cohortId, scaledMultipliers],
    query: {
      enabled,
      placeholderData: (previousData) => previousData,
    },
  });

  const distribution = useMemo((): DistributionEntry[] => {
    if (!distributionData) return [];
    const shares = distributionData[0] as bigint[];
    const total = distributionData[1] as bigint;

    return shares.map((share, index) => {
      const percentage = total > 0n ? (Number(share) / Number(total)) * 100 : 0;
      const name = participantNames[index] || `Participant ${index + 1}`;
      return { index, share, percentage, name };
    });
  }, [distributionData, participantNames]);

  return { distribution, scaledMultipliers, refetch };
}
